import { useState, useRef } from "react";
import { usePhotoUpload, ACCEPT } from "./hooks/usePhotoUpload";

type CompUploadFormProps = {
  onUploaded?: () => void;
};

export function CompUploadForm({ onUploaded }: CompUploadFormProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { progress, state, error, runUpload, retry } = usePhotoUpload({ onUploaded });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected?.length) runUpload(selected);
    e.target.value = "";
  };

  const handleClick = () => {
    if (state === "uploading") return;
    inputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (state === "uploading") return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (state === "uploading") return;
    const files = e.dataTransfer.files;
    if (files?.length) runUpload(files);
  };

  return (
    <li className="rounded-2xl bg-[var(--wedding-white)] shadow-md overflow-hidden list-none w-42 min-w-0 border-2 border-dashed border-[var(--wedding-gold-light)] hover:border-[var(--wedding-gold)] hover:shadow-[var(--wedding-shadow-hover)] transition-all duration-200">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={handleInputChange}
        className="sr-only"
        id="photos-upload"
        aria-label="Fotos auswählen"
        disabled={state === "uploading"}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative w-42 h-42 flex flex-col items-center justify-center cursor-pointer
          transition-all duration-200 select-none
          ${isDragOver ? "bg-[var(--wedding-blush)]" : "bg-[var(--wedding-cream)] hover:bg-[var(--wedding-blush)]"}
          ${state === "uploading" ? "cursor-wait pointer-events-none" : ""}
        `}
        aria-label="Fotos oder Videos per Drag & Drop oder Klick hinzufügen"
      >
        {state === "uploading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[var(--wedding-white)]/95">
            <progress
              className="w-24 h-2 rounded-full overflow-hidden bg-[var(--wedding-blush-strong)] [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-[var(--wedding-gold)]"
              value={progress}
              max={100}
              aria-label="Upload-Fortschritt"
            />
            <span className="text-sm text-[var(--wedding-ink-muted)]">{progress}%</span>
          </div>
        )}
        {state !== "uploading" && (
          <>
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[var(--wedding-gold)]/15 text-[var(--wedding-gold)]">
              <svg
                className="w-7 h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </span>
            <span className="mt-2 text-xs font-medium text-[var(--wedding-ink-muted)] text-center px-2">
              Fotos & Videos hinzufügen
            </span>
            <span className="text-[10px] text-[var(--wedding-ink-muted)]/80 mt-0.5">
              Ablegen oder tippen
            </span>
          </>
        )}
      </div>
      {state === "error" ? (
        <div
          className="p-2 flex items-center justify-between gap-2 bg-red-50 border-t border-red-100"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm text-red-700 truncate flex-1 min-w-0" role="alert">
            {error}
          </p>
          <button
            type="button"
            onClick={retry}
            className="shrink-0 rounded-lg bg-red-100 px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-200"
          >
            Erneut versuchen
          </button>
        </div>
      ) : (
        <div className="p-2 flex items-center justify-between gap-2 bg-[var(--wedding-cream)] border-t border-[var(--wedding-blush)] min-h-[2.5rem]" />
      )}
    </li>
  );
}